import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Check, X } from 'lucide-react';
import api from '../lib/api';

function SectionTitle({ children }) {
  return (
    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-4">
      {children}
    </h3>
  );
}

export default function EmailPreferences() {
  const queryClient = useQueryClient();
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Fetch employee's email preferences
  const {
    data: preferencesData = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['email-preferences'],
    queryFn: async () => {
      const res = await api.get('/api/email-preferences');
      return res.data?.data || [];
    },
  });

  // Mutation to update a preference
  const updatePreferenceMutation = useMutation({
    mutationFn: async ({ event, enabled }) => {
      const res = await api.put(`/api/email-preferences/${event}`, { enabled });
      return res.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-preferences'] });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    },
  });

  if (isError) {
    return (
      <div className="max-w-2xl mx-auto flex flex-col items-center justify-center py-20 gap-3">
        <p className="text-red-600 font-medium">Failed to load email preferences.</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Bell className="w-6 h-6 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Email Notification Preferences</h1>
        </div>
        <p className="text-gray-600">
          Choose which notifications you'd like to receive via email. Your admin controls which events are available.
        </p>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className="h-20 bg-gray-100 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : preferencesData.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">
            No email notifications are available yet. Your admin will enable events here for you to choose from.
          </p>
        </div>
      ) : (
        <>
          {/* Success Message */}
          {saveSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
              <Check className="w-5 h-5 text-green-600" />
              <p className="text-sm font-medium text-green-800">Preferences saved successfully</p>
            </div>
          )}

          {/* Preferences Grid */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <SectionTitle>Available Notifications</SectionTitle>
            <div className="grid grid-cols-1 gap-4">
              {preferencesData.map((pref) => (
                <label
                  key={pref.event}
                  className={`flex items-start gap-4 p-4 border rounded-lg cursor-pointer transition-colors ${
                    pref.enabled
                      ? 'border-blue-200 bg-blue-50 hover:bg-blue-100'
                      : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={pref.enabled}
                    onChange={(e) => {
                      updatePreferenceMutation.mutate({
                        event: pref.event,
                        enabled: e.target.checked,
                      });
                    }}
                    disabled={updatePreferenceMutation.isPending}
                    className="mt-1 w-5 h-5 text-blue-600 rounded cursor-pointer disabled:opacity-50"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">
                      {pref.event
                        .split('_')
                        .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
                        .join(' ')}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      {getEventDescription(pref.event)}
                    </p>
                  </div>
                  {pref.enabled ? (
                    <div className="flex-shrink-0 text-green-600">
                      <Check className="w-5 h-5" />
                    </div>
                  ) : (
                    <div className="flex-shrink-0 text-gray-400">
                      <X className="w-5 h-5" />
                    </div>
                  )}
                </label>
              ))}
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-xs text-blue-700">
              <strong>💡 Tip:</strong> Your admin controls which events are available. If an event isn't listed here, your admin hasn't enabled it for email notifications yet.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function getEventDescription(event) {
  const descriptions = {
    TIMER_START: 'When you start tracking time',
    TIMER_STOP: 'When you manually stop tracking time',
    TIMER_IDLE_STOP: 'When your timer auto-stops due to inactivity',
    CHECKIN: 'When you check in for the day',
    CHECKOUT: 'When you check out for the day',
    LATE_START: "When you haven't started by your usual time",
    BREAK_START: 'When you take a break',
    BREAK_END: 'When you return from a break',
    TASK_ASSIGNED: 'When a task is assigned to you',
    TASK_STATUS_CHANGED: 'When a task status is updated',
    PROJECT_MEMBER_ADDED: 'When you are added to a project',
    DAILY_SUMMARY: 'Daily end-of-day summary report',
  };

  return descriptions[event] || 'Notification event';
}
